import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Award } from '../types';

interface Props {
  award: Award;
  onSave: (fields: { name?: string; email?: string; issuedOn?: string; expires?: string | null }) => void;
  onCancel: () => void;
  pending?: boolean;
  error?: string | null;
}

/** YYYY-MM-DD for a date input, from an ISO string (or empty). */
const toDateInput = (iso?: string | null) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

/**
 * Edit an UNSENT credential in place (name / email / dates). Only available while
 * the credential has not been emailed and is not revoked — the caller gates the
 * entry point. Sends only the fields that changed.
 */
export function EditAwardModal({ award, onSave, onCancel, pending, error }: Props) {
  const { t } = useTranslation('dashboard');
  const [name, setName] = useState(award.recipient?.name || '');
  const [email, setEmail] = useState(award.recipientEmail || '');
  const [issuedOn, setIssuedOn] = useState(toDateInput(award.issuedOn));
  const [expires, setExpires] = useState(toDateInput(award.expires));

  const origName = award.recipient?.name || '';
  const origEmail = award.recipientEmail || '';
  const origIssued = toDateInput(award.issuedOn);
  const origExpires = toDateInput(award.expires);

  const nameOk = name.trim().length >= 5;
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const changed = name.trim() !== origName || email.trim() !== origEmail || issuedOn !== origIssued || expires !== origExpires;
  const canSave = nameOk && emailOk && !!issuedOn && changed && !pending;

  function submit() {
    // Only send changed fields (PATCH semantics).
    const fields: { name?: string; email?: string; issuedOn?: string; expires?: string | null } = {};
    if (name.trim() !== origName) fields.name = name.trim();
    if (email.trim() !== origEmail) fields.email = email.trim();
    if (issuedOn !== origIssued) fields.issuedOn = issuedOn;
    if (expires !== origExpires) fields.expires = expires || null;
    onSave(fields);
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-bold text-gray-900">{t('awards.editTitle', 'Edit credential')}</h2>
        <p className="mt-2 text-sm text-gray-600">
          {t('awards.editHelp', 'Fix details before this credential is emailed. Once you send it, it can no longer be edited.')}
        </p>

        <label className="mt-4 block text-xs font-medium text-gray-700">{t('awards.editName', 'Recipient name')}</label>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
        />
        {!nameOk && name.length > 0 && (
          <p className="mt-1 text-xs text-red-600">{t('awards.editNameError', 'Name must be at least 5 characters.')}</p>
        )}

        <label className="mt-3 block text-xs font-medium text-gray-700">{t('awards.editEmail', 'Recipient email')}</label>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
        />
        {!emailOk && email.length > 0 && (
          <p className="mt-1 text-xs text-red-600">{t('awards.editEmailError', 'Enter a valid email address.')}</p>
        )}

        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700">{t('awards.editIssuedOn', 'Issue date')}</label>
            <input
              type="date"
              value={issuedOn}
              onChange={e => setIssuedOn(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700">{t('awards.editExpires', 'Expires (optional)')}</label>
            <input
              type="date"
              value={expires}
              onChange={e => setExpires(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
        </div>

        {error && <p className="mt-3 text-xs text-red-600">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50">
            {t('common:buttons.cancel', 'Cancel')}
          </button>
          <button
            onClick={submit}
            disabled={!canSave}
            className="rounded-lg bg-primary-600 px-4 py-2 text-xs font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {pending ? t('awards.editSaving', 'Saving…') : t('awards.editSave', 'Save changes')}
          </button>
        </div>
      </div>
    </div>
  );
}
